import { For, JSX, Show, createMemo, createSignal, mergeProps, splitProps } from 'solid-js';

import { cx } from '../../utils';

type Tick = number | string | { value: number; label: string | number };
type NormalizedTick = { value: number; label: string | number; percent: number };

export interface SliderProps extends Omit<JSX.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  min?: number;
  max?: number;
  step?: number;
  value?: number;
  defaultValue?: number;
  onChange?: (value: number) => void;
  label?: string;
  ticks?: Tick[];
}

export function Slider(rawProps: SliderProps) {
  const props = mergeProps({ min: 0, max: 2 }, rawProps);
  const [local, others] = splitProps(props, [
    'min',
    'max',
    'step',
    'value',
    'defaultValue',
    'onChange',
    'label',
    'ticks',
    'class',
  ]);

  const [internal, setInternal] = createSignal(local.value ?? local.defaultValue ?? local.min);

  const controlledValue = () => (local.value === undefined ? internal() : local.value);
  const clamp = (val: number) => Math.min(local.max, Math.max(local.min, val));

  const percent = createMemo(() => {
    const range = local.max - local.min;
    if (range === 0) return 0;
    return ((controlledValue() - local.min) / range) * 100;
  });

  const normalizedTicks = createMemo<NormalizedTick[] | undefined>(() => {
    if (!local.ticks || local.ticks.length === 0) return undefined;
    const sliderRange = Math.max(local.max - local.min, 0);
    const distributionDenominator = Math.max(local.ticks.length - 1, 1);
    const stepForLabel = distributionDenominator === 0 ? 0 : sliderRange / distributionDenominator;

    return local.ticks.map((tick, index) => {
      let value: number;
      let label: string | number;

      if (typeof tick === 'number') {
        value = clamp(tick);
        label = tick;
      } else if (typeof tick === 'string') {
        value = clamp(local.min + stepForLabel * index);
        label = tick;
      } else {
        value = clamp(tick.value);
        label = tick.label;
      }

      const percent = sliderRange === 0 ? 0 : ((value - local.min) / sliderRange) * 100;

      return { value, label, percent };
    });
  });

  const resolvedStep = createMemo(() => {
    if (local.step !== undefined && local.step > 0) return local.step;

    const ticks = normalizedTicks();
    if (ticks && ticks.length > 1) {
      const values = [...new Set(ticks.map((tick) => tick.value))].sort((a, b) => a - b);
      let minDiff = Number.POSITIVE_INFINITY;
      for (let i = 1; i < values.length; i += 1) {
        const diff = values[i] - values[i - 1];
        if (diff > 0) {
          minDiff = Math.min(minDiff, diff);
        }
      }
      if (Number.isFinite(minDiff)) {
        return Number(minDiff.toFixed(4));
      }
    }

    const sliderRange = Math.abs(local.max - local.min);
    const fallback = sliderRange / 100;
    return Number((fallback > 0 ? fallback : 1).toFixed(4));
  });

  const handlePointerDown: JSX.EventHandlerUnion<HTMLDivElement, PointerEvent> = (event) => {
    event.preventDefault();
    const slider = event.currentTarget as HTMLElement;
    slider.setPointerCapture(event.pointerId);
    updateFromPointer(event, slider);

    const move = (e: PointerEvent) => updateFromPointer(e, slider);
    const up = (e: PointerEvent) => {
      updateFromPointer(e, slider);
      slider.releasePointerCapture(event.pointerId);
      slider.removeEventListener('pointermove', move);
      slider.removeEventListener('pointerup', up);
      slider.removeEventListener('pointercancel', up);
    };

    slider.addEventListener('pointermove', move);
    slider.addEventListener('pointerup', up);
    slider.addEventListener('pointercancel', up);
  };

  const handleKeyDown: JSX.EventHandlerUnion<HTMLDivElement, KeyboardEvent> = (event) => {
    const key = event.key;
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(key)) return;
    event.preventDefault();
    const step = resolvedStep();
    const delta =
      key === 'ArrowRight' || key === 'ArrowUp' ? step : key === 'ArrowLeft' || key === 'ArrowDown' ? -step : 0;
    let nextValue = controlledValue();
    if (key === 'Home') nextValue = local.min;
    else if (key === 'End') nextValue = local.max;
    else nextValue = nextValue + delta;
    nextValue = clamp(Number(nextValue.toFixed(4)));
    if (local.value === undefined) {
      setInternal(nextValue);
    }
    local.onChange?.(nextValue);
  };

  const updateFromPointer = (e: PointerEvent, target: HTMLElement) => {
    const rect = target.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    const rawValue = local.min + ratio * (local.max - local.min);
    const step = resolvedStep();
    console.log('step', step);
    const steppedValue = Math.round((rawValue - local.min) / step) * step + local.min;
    const nextValue = clamp(Number(steppedValue.toFixed(4)));

    if (local.value === undefined) {
      setInternal(nextValue);
    }

    local.onChange?.(nextValue);
  };

  return (
    <div class={cx('bz-slider', local.class)} {...others}>
      <Show when={local.label}>
        <div class="bz-slider__label">{local.label}</div>
      </Show>

      <div
        class="bz-slider__track"
        role="slider"
        tabIndex={0}
        aria-valuemin={local.min}
        aria-valuemax={local.max}
        aria-valuenow={controlledValue()}
        aria-label={local.label}
        onPointerDown={handlePointerDown}
        onKeyDown={handleKeyDown}
      >
        <div class="bz-slider__rail" />
        <div class="bz-slider__fill  bg-brand" style={{ width: `${percent()}%` }} />
        <div class="bz-slider__thumb" style={{ left: `${percent()}%` }}>
          {/* <span>{displayValue()}</span> */}
        </div>
      </div>

      <Show when={normalizedTicks()?.length}>
        <div class="bz-slider__ticks">
          <For each={normalizedTicks()}>
            {(tick) => {
              const isStart = tick.percent <= 0;
              const isEnd = tick.percent >= 100;
              return (
                <div
                  class={cx('bz-slider__tick', isStart && 'bz-slider__tick--start', isEnd && 'bz-slider__tick--end')}
                  style={{ left: isStart ? '-2%' : isEnd ? '102%' : `${tick.percent}%` }}
                >
                  <span
                    class={cx(
                      'bz-slider__tick-label',
                      Math.round(controlledValue()) === Math.round(tick.value) && 'is-active'
                    )}
                  >
                    {tick.label}
                  </span>
                </div>
              );
            }}
          </For>
        </div>
      </Show>
    </div>
  );
}

export default Slider;
